<template>
  <!--
    音量块：与 RtSoundPicker 共享 `bg-key rounded-4xl` 容器风格，
    承载 slider、竖线刻度与还原按钮。
  -->
  <div class="p-3 flex flex-col bg-key rounded-4xl gap-2 min-w-0">
    <h3 class="text-sm leading-5.5 font-medium text-black-white truncate">
      {{ t('rt.volume.label') }}
    </h3>

    <div class="flex-1 flex items-start gap-3 w-full">
      <div class="flex-1 relative min-w-0">
        <Slider
          :model-value="localValue === undefined ? [] : [localValue]"
          :min="min"
          :max="max"
          :step="step"
          :disabled="disabled"
          thumb-alignment="contain"
          range-alignment="thumb-center"
          :thumb-size="THUMB_WIDTH"
          animated
          class="h-6 w-full relative"
          track-class="bg-neutral-light-hover h-6"
          range-class="bg-linear-[var(--color-rt2)] rounded-none"
          thumb-class="bg-transparent rounded-none"
          @update:model-value="onDrag"
          @value-commit="onCommit"
        >
          <template #thumb>
            <div
              class="pointer-events-none relative flex items-center justify-center size-full bg-white border rounded-10xl border-brand-blue1"
            >
              <Icon
                v-if="localValue !== undefined && localValue === min"
                icon="Multimedia-Fill-Mute"
                class="size-4 text-brand-blue1"
              />
            </div>
          </template>
        </Slider>

        <div class="relative mt-2 h-3">
          <button
            v-for="tick in tickMarks"
            :key="tick.value"
            type="button"
            class="absolute inset-y-0 w-4 -translate-x-1/2 flex items-center justify-center select-none"
            :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
            :style="{
              left: `calc(${THUMB_HALF}px + ${tick.percent / 100} * (100% - ${THUMB_WIDTH}px))`,
            }"
            :disabled="disabled"
            :aria-label="t('rt.sound.setVolume', { value: tick.value, unit })"
            @click="jumpToTick(tick.value)"
          >
            <span
              v-if="tick.value === min"
              class="text-s-medium text-text-glass-default whitespace-nowrap"
            >
              {{ t('rt.common.off') }}
            </span>
            <span
              v-else-if="tick.value === max"
              class="text-s-medium text-text-glass-default whitespace-nowrap"
            >
              {{ t('rt.common.strong') }}
            </span>
            <span
              v-else
              class="block w-px h-3 transition-colors"
              :class="
                localValue !== undefined && localValue >= tick.value
                  ? 'bg-brand-blue1'
                  : 'bg-text-glass-default'
              "
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <RtRestoreButton
        color="text-b-w"
        :disabled="disabled || localValue === undefined || localValue === effectiveDefault"
        size="icon-xs"
        @confirm="resetToDefault"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Icon, Slider } from '@rapoo/element'
  import RtRestoreButton from '../RtRestoreButton.vue'

  const props = withDefaults(
    defineProps<{
      /** 当前模式（both / left / right）对应的展示值 */
      value?: number
      min?: number
      max?: number
      step?: number
      /** 刻度间隔，默认每 10% 一个刻度 */
      tickStep?: number
      /** 还原按钮的目标值，未传时回退到最小值 */
      defaultValue?: number
      unit?: string
      disabled?: boolean
    }>(),
    {
      min: 0,
      max: 100,
      step: 10,
      tickStep: 10,
      defaultValue: undefined,
      unit: '%',
      disabled: false,
    },
  )

  const emit = defineEmits<{
    /** 拖拽释放后回传新值；由父组件按 mode 落库 */
    commit: [value: number]
  }>()

  const { t } = useI18n()

  const localValue = ref<number | undefined>(props.value)
  /** 保留原 size-5.5 的 22px thumb，并统一用于 range 对齐和刻度定位。 */
  const THUMB_WIDTH = 24
  const THUMB_HALF = THUMB_WIDTH / 2

  const effectiveDefault = computed(() => props.defaultValue ?? props.min)

  /** 音量刻度默认按 10% 分段，位置与 thumb 中心保持一致。 */
  const tickMarks = computed(() => {
    const range = props.max - props.min
    if (range <= 0 || props.tickStep <= 0) return []

    const values: number[] = []
    for (let value = props.min; value < props.max; value += props.tickStep) {
      values.push(value)
    }
    values.push(props.max)

    return values.map((value) => ({
      value,
      percent: ((value - props.min) / range) * 100,
    }))
  })

  /**
   * 外部 value 变化时同步进 localValue：
   * - 父组件按 mode 切换 / SDK 推送新值都会触发这条 watcher；
   * - 「拖拽中不反向写入」，避免边拖边把 SDK 的旧值重新盖回 slider，造成抖动。
   * 用 immediate 防止首屏首值晚于一帧再绘制。
   */
  watch(
    () => props.value,
    (next) => {
      localValue.value = next
    },
    { immediate: true },
  )

  function snap(value: number) {
    const next = Math.round(value / props.step) * props.step
    return Math.min(props.max, Math.max(props.min, next))
  }

  function onDrag(value?: number[]) {
    if (value?.[0] === undefined) return
    localValue.value = snap(value[0])
  }

  function onCommit(value: number[]) {
    if (value[0] === undefined) return
    const next = snap(value[0])
    localValue.value = next
    emit('commit', next)
  }

  function jumpToTick(value: number) {
    const next = snap(value)
    localValue.value = next
    emit('commit', next)
  }

  function resetToDefault() {
    const next = snap(effectiveDefault.value)
    localValue.value = next
    emit('commit', next)
  }
</script>
